/**
 * Who owns `/`.
 *
 * The shortcut registry binds `/` to "Focus search" once, globally, and the
 * shell used to answer it by opening the command palette — while every list
 * surface printed a boxed `/` chip on its own search field and announced
 * `aria-keyshortcuts="/"` on it. Two hints, one key, and the one attached to the
 * field the reader was looking at did nothing: pressing `/` on `/tasks` put the
 * caret in "Search inquiries, contacts, companies — or type a command".
 *
 * So `/` is now scoped. A mounted `ListToolbar` search field registers itself
 * here; the shell asks this module first and only falls back to the palette when
 * no surface has claimed the key. That keeps the toolbar grammar intact — the
 * chip and the ARIA stay exactly where the grammar puts them — and makes both of
 * them true.
 *
 * Registration is a stack, so a surface that mounts over another (a split pane,
 * a drawer with its own field) takes the key and hands it back on unmount, in
 * the same shape as `pushHotkeyScope` in `lib/keyboard`.
 */

export type ScopedSearchTarget = { readonly current: HTMLInputElement | null };

const stack: ScopedSearchTarget[] = [];

/**
 * Claim `/` for this input for as long as it is mounted. Returns the release,
 * so it is a `useEffect` body in one line.
 */
export function registerScopedSearch(target: ScopedSearchTarget): () => void {
  stack.push(target);
  let released = false;
  return () => {
    if (released) return;
    released = true;
    const index = stack.lastIndexOf(target);
    if (index !== -1) stack.splice(index, 1);
  };
}

/**
 * Put the caret in the surface's own search field. `false` means nothing is
 * mounted to take it and the caller should do whatever it did before.
 */
export function focusScopedSearch(): boolean {
  for (let index = stack.length - 1; index >= 0; index -= 1) {
    const node = stack[index]?.current;
    if (node && node.isConnected) {
      node.focus();
      node.select();
      return true;
    }
  }
  return false;
}
